import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { updateContact } from "../services/contactService";
import CustomFieldInput from "./customFieldInput";
import {
  contactDefaults,
  fieldRows,
  metricRows,
  normalizeContact,
} from "./contactFormFields";
import { canWriteAnyCid } from "../utils/permissions";

// Plain contact editor. Prefills every field from the selected contact and
// saves a straight overwrite — what you type is exactly what the contact
// becomes. No match-finding, merge modes, or note appending.
const ContactUpdateDialog = ({ open, contact, onClose, onApplied }) => {
  const contactFields = useSelector(
    (state) => state.settings.value.contactFields
  );
  const auth = useSelector((state) => state.auth.value);
  const contactsWritable = canWriteAnyCid(auth);
  const [saving, setSaving] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm({ defaultValues: contactDefaults });

  useEffect(() => {
    if (open && contact) {
      reset(normalizeContact(contact));
    }
  }, [open, contact, reset]);

  const onSubmit = async (data) => {
    if (!contact) {
      return;
    }
    setSaving(true);
    try {
      const payload = { ...data, id: contact.id };
      await updateContact(payload);
      if (onApplied) {
        onApplied(payload);
      }
      onClose(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={() => onClose(false)}>
      <DialogTitle>
        Edit Contact
        {contact ? (
          <Typography variant="body2" color="text.secondary">
            {contact.name || `Contact #${contact.id}`}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {fieldRows.map((row) => (
            <Grid item xs={12} md={6} key={row.name}>
              <Controller
                name={row.name}
                control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label={row.label} />
                )}
              />
            </Grid>
          ))}
          {contactFields
            .filter((f) => !f.archived)
            .map((def) => (
              <Grid item xs={12} md={6} key={`cf-${def.id}`}>
                <Controller
                  name={`custom_fields.${def.internal_name}`}
                  control={control}
                  render={({ field }) => (
                    <CustomFieldInput
                      def={def}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Grid>
            ))}
          <Grid item xs={12}>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField {...field} fullWidth multiline minRows={4} label="Notes" />
              )}
            />
          </Grid>
          {metricRows.map((row) => (
            <Grid item xs={6} md={3} key={row.name}>
              <Controller
                name={row.name}
                control={control}
                render={({ field }) => (
                  <TextField {...field} type="number" label={row.label} fullWidth />
                )}
              />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={() => onClose(false)} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={!isDirty || saving || !contactsWritable}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactUpdateDialog;
